import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import BACKEND_URL from '../../vnog/config';

interface CharacteristicCorrectionItem {
    id: number;
    characteristic_key?: string;
    comment?: string;
    created_at: string;
    created_by_email?: string;
    estimation?: any;
}

interface EventData {
    id: number;
    order?: number;
    start_time?: string;
    end_time?: string;
    event_type: string;
    message_content?: string;
    message_sender?: string;
    other_arguments?: any;
}

interface CharacteristicCorrectionDetail {
    id: number;
    estimation: any;
    comment?: string;
    created_at: string;
    created_by_email?: string;
    characteristic_name?: string;
    characteristic_data_type?: string;
    original_estimation: any;
    simulation_data?: {
        id: number;
        token: string;
        created_at: string;
        status: string;
        address?: {
            id: number;
            street: string;
            zipcode: string;
            house_number: string;
        };
        event_log: EventData[];
    };
    characteristics_estimation_event?: EventData;
}

interface CharacteristicCorrectionsProps {
    onLoadingChange?: (loading: boolean) => void;
}

const CorrectionDetailPanel: React.FC<{ detail: CharacteristicCorrectionDetail | null }> = ({ detail }) => {
    if (!detail) {
        return (
            <div className="bg-white rounded-lg shadow p-6">
                <div className="text-gray-500 text-center">Select a correction to view details</div>
            </div>
        );
    }

    const targetEventId = detail.characteristics_estimation_event?.id;

    return (
        <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Characteristic Correction Details</h3>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-4">
                    <div>
                        <div className="text-sm text-gray-500 mb-1">Characteristic</div>
                        <div className="text-sm font-medium">{detail.characteristic_name || '-'}</div>
                    </div>

                    {detail.characteristic_data_type && (
                        <div>
                            <div className="text-sm text-gray-500 mb-1">Data Type</div>
                            <div className="text-sm text-gray-700">{detail.characteristic_data_type}</div>
                        </div>
                    )}

                    <div>
                        <div className="text-sm text-gray-500 mb-1">Comment</div>
                        <div className="text-sm text-gray-700 break-words">{detail.comment || 'No comment provided'}</div>
                    </div>

                    <div>
                        <div className="text-sm text-gray-500 mb-1">Created By</div>
                        <div className="text-sm text-gray-700">{detail.created_by_email || 'Unknown'}</div>
                    </div>

                    <div>
                        <div className="text-sm text-gray-500 mb-1">Created At</div>
                        <div className="text-sm text-gray-700">{new Date(detail.created_at).toLocaleString()}</div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <div className="text-sm text-gray-500 mb-1">Original Estimation</div>
                            <div className="bg-gray-50 p-3 rounded text-xs">
                                <pre className="whitespace-pre-wrap break-words">
                                    {JSON.stringify(detail.original_estimation, null, 2)}
                                </pre>
                            </div>
                        </div>
                        <div>
                            <div className="text-sm text-gray-500 mb-1">Corrected Estimation</div>
                            <div className="bg-blue-50 p-3 rounded border border-blue-200 text-xs">
                                <pre className="whitespace-pre-wrap break-words">
                                    {JSON.stringify(detail.estimation, null, 2)}
                                </pre>
                            </div>
                        </div>
                    </div>

                    {detail.characteristics_estimation_event && (
                        <div>
                            <div className="text-sm text-gray-500 mb-1">Characteristics Estimation Event</div>
                            <div className="bg-gray-50 p-3 rounded">
                                <div className="text-sm"><strong>Event ID:</strong> {detail.characteristics_estimation_event.id}</div>
                                <div className="text-sm"><strong>Type:</strong> {detail.characteristics_estimation_event.event_type}</div>
                            </div>
                        </div>
                    )}

                    {detail.simulation_data && (
                        <div>
                            <div className="text-sm text-gray-500 mb-1">Simulation Details</div>
                            <div className="bg-gray-50 p-3 rounded">
                                <div className="text-sm"><strong>Simulation ID:</strong> {detail.simulation_data.id}</div>
                                <div className="text-sm"><strong>Status:</strong> {detail.simulation_data.status}</div>
                                <div className="text-sm"><strong>Created:</strong> {new Date(detail.simulation_data.created_at).toLocaleString()}</div>
                                {detail.simulation_data.address && (
                                    <div className="text-sm">
                                        <strong>Address:</strong> {detail.simulation_data.address.street} {detail.simulation_data.address.house_number}, {detail.simulation_data.address.zipcode}
                                    </div>
                                )}
                                <div className="text-sm"><strong>Events:</strong> {detail.simulation_data.event_log.length} events</div>
                            </div>
                        </div>
                    )}
                </div>

                {detail.simulation_data && detail.simulation_data.event_log && detail.simulation_data.event_log.length > 0 && (
                    <div>
                        <div className="text-sm text-gray-500 mb-1">Event Log</div>
                        <div className="bg-gray-50 p-3 rounded max-h-96 overflow-y-auto">
                            <div className="space-y-2">
                                {detail.simulation_data.event_log.map((event, index) => {
                                    const isTargetEvent = targetEventId != null && event.id === targetEventId;
                                    return (
                                        <div
                                            key={event.id}
                                            className={`p-3 rounded border ${
                                                isTargetEvent
                                                    ? 'bg-blue-100 border-blue-300 ring-2 ring-blue-400'
                                                    : 'bg-white border-gray-200'
                                            }`}
                                        >
                                            <div className="flex items-center justify-between mb-2">
                                                <div className="flex items-center space-x-2">
                                                    <span className={`text-xs px-2 py-1 rounded ${
                                                        isTargetEvent
                                                            ? 'bg-blue-200 text-blue-800'
                                                            : 'bg-gray-200 text-gray-700'
                                                    }`}>
                                                        #{index + 1}
                                                    </span>
                                                    <span className={`text-xs px-2 py-1 rounded ${
                                                        event.event_type === 'ai_operator'
                                                            ? 'bg-green-100 text-green-800'
                                                            : event.event_type === 'message'
                                                            ? 'bg-purple-100 text-purple-800'
                                                            : event.event_type === 'characteristics_estimation'
                                                            ? 'bg-indigo-100 text-indigo-800'
                                                            : 'bg-gray-100 text-gray-800'
                                                    }`}>
                                                        {event.event_type}
                                                    </span>
                                                    {isTargetEvent && (
                                                        <span className="text-xs px-2 py-1 rounded bg-yellow-100 text-yellow-800 font-medium">
                                                            Estimation Event
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="text-xs text-gray-500">
                                                    ID: {event.id}
                                                </div>
                                            </div>

                                            {event.message_content && (
                                                <div className="mb-2">
                                                    <div className="text-xs text-gray-500 mb-1">Message:</div>
                                                    <div className="text-sm text-gray-700 break-words">{event.message_content}</div>
                                                </div>
                                            )}

                                            {event.message_sender && (
                                                <div className="mb-2">
                                                    <div className="text-xs text-gray-500 mb-1">Sender:</div>
                                                    <div className="text-sm text-gray-700">{event.message_sender}</div>
                                                </div>
                                            )}

                                            {event.other_arguments && Object.keys(event.other_arguments).length > 0 && (
                                                <div className="mb-2">
                                                    <div className="text-xs text-gray-500 mb-1">Additional Data:</div>
                                                    <div className="text-xs text-gray-600 bg-gray-100 p-2 rounded">
                                                        <pre className="whitespace-pre-wrap break-words">
                                                            {JSON.stringify(event.other_arguments, null, 2)}
                                                        </pre>
                                                    </div>
                                                </div>
                                            )}

                                            <div className="flex items-center justify-between text-xs text-gray-500">
                                                {event.start_time && (
                                                    <div>Start: {new Date(event.start_time).toLocaleString()}</div>
                                                )}
                                                {event.end_time && (
                                                    <div>End: {new Date(event.end_time).toLocaleString()}</div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

const CharacteristicCorrections: React.FC<CharacteristicCorrectionsProps> = ({ onLoadingChange }) => {
    const [corrections, setCorrections] = useState<CharacteristicCorrectionItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [detailsCache, setDetailsCache] = useState<Map<number, CharacteristicCorrectionDetail>>(new Map());
    const [loadingDetail, setLoadingDetail] = useState(false);

    const fetchList = async () => {
        setLoading(true);
        onLoadingChange?.(true);
        try {
            const response = await fetch(`${BACKEND_URL}/vnog/admin/feedback/characteristic_corrections/`, {
                credentials: 'include',
            });
            if (response.ok) {
                setCorrections(await response.json());
            } else {
                toast.error('Failed to fetch characteristic corrections');
                setCorrections([]);
            }
        } catch {
            toast.error('Error fetching characteristic corrections');
            setCorrections([]);
        } finally {
            setLoading(false);
            onLoadingChange?.(false);
        }
    };

    const fetchDetail = async (id: number) => {
        if (detailsCache.has(id)) {
            setSelectedId(id);
            return;
        }
        setLoadingDetail(true);
        try {
            const response = await fetch(`${BACKEND_URL}/vnog/admin/feedback/characteristic_corrections/${id}/`, {
                credentials: 'include',
            });
            if (response.ok) {
                const detail = await response.json();
                setDetailsCache(prev => new Map(prev).set(id, detail));
                setSelectedId(id);
            } else {
                toast.error('Failed to fetch correction details');
            }
        } catch {
            toast.error('Error fetching correction details');
        } finally {
            setLoadingDetail(false);
        }
    };

    useEffect(() => {
        fetchList();
    }, []);

    if (loading) {
        return <div className="px-6 py-4 bg-white rounded-lg shadow">Loading…</div>;
    }

    if (corrections.length === 0) {
        return <div className="px-6 py-4 bg-white rounded-lg shadow text-gray-500">No data</div>;
    }

    const selectedDetail = selectedId ? detailsCache.get(selectedId) : null;

    return (
        <div className="space-y-4">
            <h2 className="text-2xl font-semibold mb-1">Characteristic Estimation Corrections</h2>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                <div className="lg:col-span-1">
                    <div className="space-y-3">
                        {corrections.map((item) => (
                            <div
                                key={item.id}
                                className={`bg-white rounded-lg shadow p-4 cursor-pointer transition-colors ${
                                    selectedId === item.id
                                        ? 'ring-2 ring-blue-500 bg-blue-50'
                                        : 'hover:bg-gray-50'
                                }`}
                                onClick={() => fetchDetail(item.id)}
                            >
                                <div className="text-sm text-gray-500 mb-1">Characteristic</div>
                                <div className="text-sm font-medium text-gray-900 mb-2 break-words">
                                    {item.characteristic_key || '-'}
                                </div>
                                <div className="text-sm text-gray-500 mb-1">Comment</div>
                                <div className="text-sm text-gray-700 mb-2 break-words line-clamp-2">
                                    {item.comment || 'No comment'}
                                </div>
                                <div className="flex items-center justify-between text-xs text-gray-500">
                                    <div>By: {item.created_by_email || '-'}</div>
                                    <div>{new Date(item.created_at).toLocaleDateString()}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="lg:col-span-3">
                    {loadingDetail ? (
                        <div className="bg-white rounded-lg shadow p-6">
                            <div className="text-center">Loading details...</div>
                        </div>
                    ) : (
                        <CorrectionDetailPanel detail={selectedDetail || null} />
                    )}
                </div>
            </div>
        </div>
    );
};

export default CharacteristicCorrections;
